import type { EAnalyticMode, AnalyticInterval } from '~/modules/analytics/types';
import type { AnalyticDateFilter } from '~/presentation/ui';
import type { ICampaignAnalytic } from '~/types/metric';

import { useCustomCampaignAnalyticQueryV2 } from '../api/useCustomCampaignAnalyticQueryV2';
import { extractCampaignVariantData } from '../helpers/extractCampaignVariantData';
import { buildCampaignListingQueryVariables } from '../helpers/campaignAnalyticV2';
import { TIME_DIMENSION_TO_INTERVAL } from '../helpers/timeDimensionToInterval';
import { getTimeDimensionByDate } from '~/core/gemxql/helpers';
import { useMemo } from 'react';

export interface IFetchCampaignAnalyticsV2Props {
  campaignVariantIdA: string;
  campaignVariantIdB: string;
  /** Resolved from the campaign's own versions; scopes the current period only. */
  campaignVersionID?: string;
  currentDates: AnalyticDateFilter;
  previousDates: AnalyticDateFilter;
  isQueryPreviousData?: boolean;
  analyticMode: EAnalyticMode;
}

export interface IFetchCampaignAnalyticsV2Result {
  campaignAnalyticA: ICampaignAnalytic[];
  campaignAnalyticB: ICampaignAnalytic[];
  previousCampaignAnalyticA: ICampaignAnalytic[];
  previousCampaignAnalyticB: ICampaignAnalytic[];
  isLoading: boolean;
  interval: AnalyticInterval;
}

export const useFetchCampaignAnalyticsV2 = ({
  campaignVariantIdA,
  campaignVariantIdB,
  campaignVersionID,
  currentDates,
  previousDates,
  isQueryPreviousData = true,
  analyticMode,
}: IFetchCampaignAnalyticsV2Props): IFetchCampaignAnalyticsV2Result => {
  const timeDimension = useMemo(
    () => getTimeDimensionByDate(currentDates.startDate, currentDates.endDate),
    [currentDates.startDate, currentDates.endDate],
  );

  const variantIds = useMemo(
    () => [campaignVariantIdA, campaignVariantIdB].filter(Boolean),
    [campaignVariantIdA, campaignVariantIdB],
  );

  // Only the current period is version-scoped; the compare window spans other versions by design.
  const currentVariables = useMemo(
    () =>
      buildCampaignListingQueryVariables({
        mode: analyticMode,
        campaignVariantIDs: variantIds,
        campaignVersionID,
        dateRange: currentDates,
      }),
    [analyticMode, variantIds, campaignVersionID, currentDates],
  );

  const previousVariables = useMemo(
    () =>
      buildCampaignListingQueryVariables({
        mode: analyticMode,
        campaignVariantIDs: variantIds,
        dateRange: previousDates,
      }),
    [analyticMode, variantIds, previousDates],
  );

  const isEnabled = variantIds.length > 0 && !!currentVariables.input;
  const isEnabledPrevious = isEnabled && isQueryPreviousData && !!previousVariables.input;

  const { data: currentResponse, isLoading } = useCustomCampaignAnalyticQueryV2(currentVariables, {
    enabled: isEnabled,
  });

  const { data: previousResponse, isLoading: isLoadingPrevious } = useCustomCampaignAnalyticQueryV2(
    previousVariables,
    {
      enabled: isEnabledPrevious,
    },
  );

  const current = useMemo(
    () =>
      extractCampaignVariantData({
        response: currentResponse,
        campaignVariantIdA,
        campaignVariantIdB,
      }),
    [currentResponse, campaignVariantIdA, campaignVariantIdB],
  );

  const previous = useMemo(
    () =>
      extractCampaignVariantData({
        response: previousResponse,
        campaignVariantIdA,
        campaignVariantIdB,
      }),
    [previousResponse, campaignVariantIdA, campaignVariantIdB],
  );

  return {
    campaignAnalyticA: current.analyticA,
    campaignAnalyticB: current.analyticB,
    previousCampaignAnalyticA: previous.analyticA,
    previousCampaignAnalyticB: previous.analyticB,
    isLoading: (isEnabled && isLoading) || (isEnabledPrevious && isLoadingPrevious),
    interval: TIME_DIMENSION_TO_INTERVAL[timeDimension],
  };
};
